import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

export type ReconcileSubResult = {
  matches_found: number;
  deleted: number;
  created: number;
  active_killed?: boolean;
};

export type PerContractResult = {
  contrato_id: string;
  codigo: string;
  cliente_nombre?: string | null;
  success: boolean;
  anomalies: string[];
  error?: string;
};

export type ReconcileResult = {
  success: boolean;
  action: string;
  secret?: ReconcileSubResult;
  profile?: ReconcileSubResult;
  address_list?: ReconcileSubResult;
  filter_rule?: ReconcileSubResult;
  rules?: ReconcileSubResult;
  // Campos de reconcile_full_router
  contracts_processed?: number;
  contracts_ok?: number;
  contracts_error?: number;
  per_contract?: PerContractResult[];
  anomalies: string[];
  error?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  result: ReconcileResult | null;
  title?: string;
};

/**
 * Modal que muestra el resultado estructurado del motor de reconciliación
 * (F15). Reutilizado desde contratos (reintentar provisionar) y routers
 * (recrear reglas). Presenta métricas por sección y lista de anomalías.
 */
export function ReconcileResultDialog({ open, onOpenChange, result, title }: Props) {
  if (!result) return null;
  const sections: { label: string; data: ReconcileSubResult }[] = (
    [
      { label: "Profile", data: result.profile },
      { label: "Secret", data: result.secret },
      { label: "Address-list", data: result.address_list },
      { label: "Filter rules (suspensión)", data: result.filter_rule },
      { label: "Rules", data: result.rules },
    ] as { label: string; data: ReconcileSubResult | undefined }[]
  ).filter(
    (s): s is { label: string; data: ReconcileSubResult } => s.data !== undefined
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {result.success ? (
            <CheckCircle2 size={16} className="text-emerald-600" />
          ) : (
            <XCircle size={16} className="text-destructive" />
          )}
          {title ?? "Resultado de reconciliación"}
        </DialogTitle>
        <DialogDescription>
          {result.success
            ? "El router quedó consistente con el estado esperado. Revisa abajo los cambios aplicados."
            : "La reconciliación falló. Revisa el error y las anomalías."}
        </DialogDescription>
      </DialogHeader>

      <DialogContent className="flex flex-col gap-3">
        {result.error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
            <XCircle size={14} className="mt-0.5 shrink-0" />
            <span>{result.error}</span>
          </div>
        )}

        {sections.length > 0 && (
          <div className="rounded-md border">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Sección</th>
                  <th className="px-2 py-1.5 text-right font-medium">Encontrados</th>
                  <th className="px-2 py-1.5 text-right font-medium">Borrados</th>
                  <th className="px-2 py-1.5 text-right font-medium">Creados</th>
                  <th className="px-2 py-1.5 text-center font-medium">Kill active</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((s) => (
                  <tr key={s.label} className="border-t">
                    <td className="px-2 py-1.5 font-medium">{s.label}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {s.data.matches_found}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {s.data.deleted}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {s.data.created}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {s.data.active_killed === true ? (
                        <Badge variant="warning" className="text-[10px]">
                          sí
                        </Badge>
                      ) : s.data.active_killed === false ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Resumen per-contrato (solo para reconcile_full_router) */}
        {result.per_contract && result.per_contract.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium">Contratos:</span>
              <Badge variant="success" className="text-[10px]">
                {result.contracts_ok ?? 0} OK
              </Badge>
              {(result.contracts_error ?? 0) > 0 && (
                <Badge variant="destructive" className="text-[10px]">
                  {result.contracts_error} con error
                </Badge>
              )}
              <span className="text-muted-foreground">
                de {result.contracts_processed ?? 0} procesados
              </span>
            </div>
            <div className="max-h-[220px] overflow-y-auto rounded-md border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Contrato</th>
                    <th className="px-2 py-1.5 text-left font-medium">Cliente</th>
                    <th className="px-2 py-1.5 text-center font-medium">Estado</th>
                    <th className="px-2 py-1.5 text-left font-medium">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {result.per_contract.map((pc) => (
                    <tr key={pc.contrato_id} className="border-t">
                      <td className="px-2 py-1 font-mono">{pc.codigo}</td>
                      <td className="px-2 py-1 truncate max-w-[180px]" title={pc.cliente_nombre ?? ""}>
                        {pc.cliente_nombre ?? <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {pc.success ? (
                          <CheckCircle2 size={12} className="inline text-emerald-600" />
                        ) : (
                          <XCircle size={12} className="inline text-destructive" />
                        )}
                      </td>
                      <td className="px-2 py-1 text-muted-foreground">
                        {pc.error
                          ? pc.error
                          : pc.anomalies.length > 0
                          ? `${pc.anomalies.length} anomalía(s)`
                          : "OK sin anomalías"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result.anomalies && result.anomalies.length > 0 && (
          <div className="flex flex-col gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs dark:border-amber-900/40 dark:bg-amber-950/30">
            <div className="flex items-center gap-1.5 font-medium text-amber-900 dark:text-amber-300">
              <AlertTriangle size={13} />
              Anomalías detectadas ({result.anomalies.length})
            </div>
            <ul className="ml-5 list-disc text-amber-900/80 dark:text-amber-200/80">
              {result.anomalies.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        {result.success && result.anomalies.length === 0 && !result.error && (
          <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300">
            <CheckCircle2 size={13} />
            Sin anomalías. Router consistente.
          </div>
        )}
      </DialogContent>

      <DialogFooter>
        <Button size="sm" onClick={() => onOpenChange(false)}>
          Cerrar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
